/**
 * useScreenshotDetect — desktop-only listener that catches the keyboard
 * shortcuts users press to take screenshots and fires a fire-and-forget
 * audit log entry to the backend. Browser caveat: NONE of these can
 * actually BLOCK the screenshot — the OS captures the screen at a layer
 * below the browser. We can only OBSERVE the keystroke and log it.
 *
 * What we catch:
 *   - PrintScreen / PrintScrn key (Windows + many Linux DEs)
 *   - Cmd+Shift+3 (macOS: full-screen capture)
 *   - Cmd+Shift+4 (macOS: rectangular selection)
 *   - Cmd+Shift+5 (macOS: screenshot/screen-recording toolbar)
 *   - Win+Shift+S (Windows 10/11 Snipping Tool)
 *
 * What we CANNOT catch:
 *   - iOS / Android in-browser screenshots (no API exposed to the web)
 *   - Third-party screenshot apps (Lightshot, Greenshot, Snagit) that
 *     don't use the OS shortcut
 *   - Browser DevTools screenshot tool
 *   - Camera-pointed-at-monitor (the OG analog hole)
 *
 * Throttled to 1 log per 4s per session so a user spamming PrintScreen
 * doesn't fill audit_log. (User ask 2026-05-21.)
 */
import { useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const THROTTLE_MS = 4000;

export default function useScreenshotDetect(crewId, currentScreen = "") {
  const lastFire = useRef(0);

  useEffect(() => {
    if (!crewId) return undefined;
    const isMac = /Mac|iPhone|iPad/i.test(navigator.platform || "");

    const fire = (method) => {
      const now = Date.now();
      if (now - lastFire.current < THROTTLE_MS) return;
      lastFire.current = now;
      // Fire-and-forget; never throw from a key handler.
      try {
        axios.post(`${API}/audit/screenshot-attempt`, {
          crew_id: crewId,
          method,
          screen: currentScreen || (typeof window !== "undefined" ? window.location.pathname : ""),
        }).catch(() => {});
      } catch (_) { /* ignore */ }
    };

    const onKey = (e) => {
      // PrintScreen — Windows + Linux. Some keyboards don't fire keydown
      // for PrintScreen, only keyup, so we listen for both via a separate
      // listener below.
      if (e.key === "PrintScreen") {
        fire("printscreen");
        return;
      }
      // macOS shortcuts. e.metaKey = Cmd.
      if (isMac && e.metaKey && e.shiftKey) {
        if (e.key === "3" || e.code === "Digit3") return fire("cmd_shift_3");
        if (e.key === "4" || e.code === "Digit4") return fire("cmd_shift_4");
        if (e.key === "5" || e.code === "Digit5") return fire("cmd_shift_5");
      }
      // Windows Snipping Tool — Win+Shift+S. The Windows key surfaces as
      // metaKey on Chrome/Edge for Windows.
      if (!isMac && e.metaKey && e.shiftKey && (e.key === "S" || e.key === "s")) {
        return fire("win_shift_s");
      }
    };

    const onKeyUp = (e) => {
      // Defensive: PrintScreen sometimes only fires on keyup.
      if (e.key === "PrintScreen") fire("printscreen");
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [crewId, currentScreen]);
}
